/**
 * Google Analytics 4 Event Tracking Helper
 * Author: MCGdev
 * 
 * This utility provides a centralized interface for tracking user interactions
 * with Google Analytics 4 (GA4) across the Just One PC Repair website.
 */

// Check if gtag is available (Google Analytics loaded)
const isGALoaded = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event in Google Analytics 4
 * @param {string} eventName - The name of the event (e.g., 'form_submit', 'phone_call')
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGALoaded()) {
    console.log('[Analytics - Dev Mode]', eventName, eventParams);
    return;
  }

  try {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[Analytics Error]', error);
  }
};

/**
 * Track form submission events
 * @param {string} formName - Name of the form submitted (e.g., 'contact_form')
 * @param {object} additionalData - Additional form data to track
 */
export const trackFormSubmission = (formName, additionalData = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    event_category: 'engagement',
    event_label: 'Form Submission',
    ...additionalData,
  });
};

/**
 * Track phone call button clicks
 * @param {string} phoneNumber - The phone number being called
 * @param {string} location - Where the call button was clicked (e.g., 'hero', 'contact_section')
 */
export const trackPhoneCall = (phoneNumber, location = 'unknown') => {
  trackEvent('phone_call_click', {
    phone_number: phoneNumber,
    click_location: location,
    event_category: 'engagement',
    event_label: 'Phone Call Initiated',
  });
};

/**
 * Track messaging app clicks (WhatsApp, Viber, Messenger)
 * @param {string} appName - Name of the app (e.g., 'WhatsApp', 'Viber')
 * @param {string} location - Where the button was clicked
 */
export const trackMessagingAppClick = (appName, location = 'unknown') => {
  trackEvent('messaging_app_click', {
    app_name: appName,
    click_location: location,
    event_category: 'engagement',
    event_label: `${appName} Message Initiated`,
  });
};

/**
 * Track page view (for SPA navigation)
 * @param {string} pagePath - The path of the page being viewed
 * @param {string} pageTitle - The title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGALoaded()) {
    console.log('[Analytics - Dev Mode] Page View:', pagePath, pageTitle);
    return;
  }

  try {
    window.gtag('config', window.GA_MEASUREMENT_ID || 'G-XXXXXXXXXX', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  } catch (error) {
    console.error('[Analytics Error]', error);
  }
};

/**
 * Track scroll depth (25%, 50%, 75%, 100%)
 * Auto-initializes on mount
 */
let scrollTracked = {
  25: false,
  50: false,
  75: false,
  100: false,
};

export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;

  const trackScrollDepth = () => {
    const scrollPercent = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );

    // Track milestones
    [25, 50, 75, 100].forEach((milestone) => {
      if (scrollPercent >= milestone && !scrollTracked[milestone]) {
        scrollTracked[milestone] = true;
        trackEvent('scroll_depth', {
          scroll_depth: milestone,
          event_category: 'engagement',
          event_label: `Scrolled ${milestone}%`,
        });
      }
    });
  };

  // Throttle scroll events
  let scrollTimeout;
  const handleScroll = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScrollDepth, 200);
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

/**
 * Track service card interactions
 * @param {string} serviceName - Name of the service clicked
 */
export const trackServiceClick = (serviceName) => {
  trackEvent('service_click', {
    service_name: serviceName,
    event_category: 'engagement',
    event_label: 'Service Card Clicked',
  });
};

/**
 * Track gallery image views
 * @param {string} imageName - Name or description of the image
 * @param {number} imageIndex - Index of the image in gallery
 */
export const trackGalleryView = (imageName, imageIndex) => {
  trackEvent('gallery_view', {
    image_name: imageName,
    image_index: imageIndex,
    event_category: 'engagement',
    event_label: 'Gallery Image Viewed',
  });
};

/**
 * Track errors or exceptions
 * @param {string} errorDescription - Description of the error
 * @param {boolean} isFatal - Whether the error is fatal
 */
export const trackError = (errorDescription, isFatal = false) => {
  trackEvent('exception', {
    description: errorDescription,
    fatal: isFatal,
    event_category: 'error',
  });
};

/**
 * Track outbound link clicks
 * @param {string} url - The URL being navigated to
 * @param {string} linkText - The text of the link clicked
 */
export const trackOutboundLink = (url, linkText = '') => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText,
    outbound: true,
  });
};

/**
 * Track cookie consent events
 * @param {string} action - 'accept' or 'decline'
 */
export const trackCookieConsent = (action) => {
  trackEvent('cookie_consent', {
    consent_action: action,
    event_category: 'privacy',
    event_label: `Cookie Consent ${action === 'accept' ? 'Accepted' : 'Declined'}`,
  });
};

// Development Mode Helper
export const enableDebugMode = () => {
  if (typeof window !== 'undefined') {
    window.GA_DEBUG_MODE = true;
    console.log('[Analytics] Debug mode enabled');
  }
};

// Export all tracking functions as default object
const analytics = {
  trackEvent,
  trackFormSubmission,
  trackPhoneCall,
  trackMessagingAppClick,
  trackPageView,
  initScrollTracking,
  trackServiceClick,
  trackGalleryView,
  trackError,
  trackOutboundLink,
  trackCookieConsent,
  enableDebugMode,
};

export default analytics;
